import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { Copy, Rocket, ClipboardCheck, Loader2, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Turn the campaign's course + strategy + extras into systeme.io-ready, build-order blocks.
function buildFunnel({ course, strategy, extras }) {
  const blocks = [];
  const lm = extras?.lead_magnet;
  const email = extras?.email;
  const tiers = strategy?.tiers || [];

  if (lm) {
    blocks.push({
      label: "OPT-IN / SQUEEZE PAGE",
      note: "Paste into your opt-in step's headline, text and button fields.",
      fields: [
        { label: "Headline", value: lm.title || "" },
        { label: "Sub-headline", value: course?.promise || "Get the free guide that gets you started fast." },
        { label: "Body", value: lm.content || "", multiline: true },
        { label: "Button text", value: "Get instant access" },
        { label: "Confirmation message", value: "Check your inbox — your free guide is on the way." },
      ],
    });
  }

  if (tiers.length) {
    const fields = [
      { label: "Headline", value: course?.promise || course?.title || "" },
      { label: "Sub-headline", value: "Choose the level of support that fits where you are right now." },
    ];
    tiers.forEach((t) => {
      const parts = [];
      if (t.description) parts.push(t.description);
      if (t.find) parts.push(`Find: ${t.find}`);
      if (t.build) parts.push(`Build: ${t.build}`);
      if (t.launch) parts.push(`Launch: ${t.launch}`);
      if (t.modules?.length) parts.push(`Includes: ${t.modules.join(", ")}`);
      fields.push({
        label: `Offer — ${t.name || ""} · ${t.price || ""} · ${t.label || ""}`.replace(/\s+·\s+·\s+/g, " · "),
        value: parts.join("\n"),
        multiline: true,
      });
    });
    fields.push({ label: "Button text", value: "Enroll now" });
    blocks.push({
      label: "SALES PAGE",
      note: "Paste the headline, then each tier into a pricing/offer block.",
      fields,
    });
  }

  if (email) {
    blocks.push({
      label: "EMAIL 1 (broadcast or automation)",
      note: "Create an email campaign step and paste the subject and body.",
      fields: [
        { label: "Subject", value: email.subject || "" },
        { label: "Body", value: email.body || "", multiline: true },
      ],
    });
  }

  return blocks;
}

const fieldToText = (f) => `[${f.label}]${f.multiline ? "\n" : " "}${f.value}`;
const blockToText = (b) => `════ ${b.label} ════\n${b.fields.map(fieldToText).join("\n\n")}`;

export const SystemeExport = ({ cid, course, strategy, extras, copy, systemeKeySet }) => {
  const navigate = useNavigate();
  const blocks = buildFunnel({ course, strategy, extras });
  const [pushing, setPushing] = useState(false);
  const [result, setResult] = useState(null);
  if (!blocks.length) return null;

  const allText = blocks.map(blockToText).join("\n\n\n");

  const pushToSysteme = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(`/campaigns/${cid}/push-to-systeme`);
      setResult(data);
      toast.success("Funnel created in your systeme.io account!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not push to systeme.io");
    } finally {
      setPushing(false);
    }
  };

  return (
    <Card className="mt-6 border-primary/20 bg-primary/5 p-6" data-testid="systeme-export">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Rocket size={22} />
          </span>
          <div>
            <h3 className="font-head text-xl font-bold">systeme.io export</h3>
            <p className="mt-0.5 max-w-xl text-sm text-muted-foreground">
              Push a ready-made funnel to your systeme.io account, or copy each block into your pages.
            </p>
            <AffiliateDisclosure className="mt-1.5" />
          </div>
        </div>
        <Button onClick={() => copy(allText, "Full funnel copied!")} data-testid="systeme-copy-all"
          variant="outline" className="shrink-0 rounded-full">
          <Copy size={15} className="mr-1.5" /> Copy entire funnel
        </Button>
      </div>

      {/* One-click push */}
      <div className="mt-5 rounded-xl border border-primary/20 bg-card p-5" data-testid="systeme-push-panel">
        {result ? (
          <div data-testid="systeme-push-result">
            <p className="flex items-center gap-2 font-semibold text-accent">
              <CheckCircle2 size={18} /> Funnel “{result.funnel?.name}” created in systeme.io
            </p>
            <ul className="mt-3 space-y-1.5 text-sm">
              {(result.steps || []).map((s, i) => (
                <li key={s.name || i} className="flex items-center gap-2">
                  <span className="font-medium">{s.name}</span>
                  {s.url && (
                    <a href={s.url} target="_blank" rel="noopener noreferrer" data-testid={`systeme-step-link-${i}`}
                      className="inline-flex items-center gap-1 text-primary hover:underline">
                      open <ExternalLink size={12} />
                    </a>
                  )}
                </li>
              ))}
              {result.page_fill === "filled" && <li data-testid="systeme-pagefill">✅ Page copy written to your opt-in & sales pages</li>}
              {result.newsletter && <li>✅ Promo email saved as a newsletter draft: “{result.newsletter.subject}”</li>}
              {result.tag && <li>✅ Opt-in tag created: “{result.tag.name}”</li>}
              {result.product && <li data-testid="systeme-product">✅ {result.product.plans} price {result.product.plans === 1 ? "plan" : "plans"} created under product “{result.product.name}”</li>}
              {result.automation && <li data-testid="systeme-automation">✅ Automation: on {result.automation.on}, {result.automation.does}</li>}
            </ul>
            {result.page_fill !== "filled" && (
              <p className="mt-3 rounded-lg bg-secondary/50 px-3 py-2 text-xs text-muted-foreground">
                Your funnel is built, but page copy couldn't be written automatically — this happens if the Page Editor API isn't enabled on your systeme.io account yet. Open each page above and paste the matching block below. Auto-fill will kick in on your next push once it's enabled.
              </p>
            )}
          </div>
        ) : systemeKeySet ? (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              Creates a funnel (opt-in → sales → thank-you), auto-fills your opt-in & sales page copy, adds price plans for your tiers, saves your promo email as a newsletter, adds an opt-in tag, and wires an automation to tag new leads.
            </p>
            <Button onClick={pushToSysteme} disabled={pushing} data-testid="systeme-push-btn"
              className="shrink-0 rounded-full bg-primary hover:bg-primary/90">
              {pushing ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <Rocket size={15} className="mr-1.5" />}
              {pushing ? "Building your funnel…" : "Send to systeme.io"}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between" data-testid="systeme-no-key">
            <p className="text-sm text-muted-foreground">
              Connect your systeme.io API key to push this funnel automatically.
            </p>
            <Button onClick={() => navigate("/settings")} variant="outline" data-testid="systeme-connect-btn"
              className="shrink-0 rounded-full">
              Connect systeme.io
            </Button>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-4">
        {blocks.map((b) => {
          const s = slug(b.label);
          return (
            <div key={s} data-testid={`systeme-block-${s}`}
              className="rounded-xl border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ClipboardCheck size={15} className="text-primary" />
                  <span className="font-head text-sm font-bold uppercase tracking-wide">{b.label}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => copy(blockToText(b), `${b.label} copied!`)}
                  data-testid={`systeme-copy-block-${s}`} className="rounded-full">
                  <Copy size={13} className="mr-1" /> Copy block
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{b.note}</p>
              <div className="mt-3 space-y-2.5">
                {b.fields.map((f, i) => (
                  <div key={f.label || i} className="flex items-start gap-2 rounded-lg border border-border/70 bg-secondary/30 p-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{f.label}</p>
                      <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{f.value}</p>
                    </div>
                    <button onClick={() => copy(f.value, "Copied!")}
                      data-testid={`systeme-copy-field-${s}-${i}`} title="Copy this field"
                      className="shrink-0 rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
                      <Copy size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {!strategy?.tiers?.length && (
        <p className="mt-4 rounded-lg bg-secondary/50 px-3 py-2 text-xs text-muted-foreground" data-testid="systeme-missing-strategy">
          Tip: generate the <span className="font-semibold text-foreground">Sales Strategy</span> tab to add your sales-page copy to this export.
        </p>
      )}
    </Card>
  );
};
